import type {
  GenericError,
  AgencyList,
  UserDetails,
  JobsData,
  EarningsData,
  PaymentDetails,
  EarningsGraphData,
  ReferralStats,
  RecentActivity,
  ReferralLinks,
} from 'src/types/referrer-portal';
import type {
  ReferralPrograms,
  GetReferralTrackingApiResponse,
  ReferralLeaderBoard,
  ReferralSelectedQuestionnaire,
} from 'src/types/referral-v2';
import type {JobDescription} from 'src/types/referral-external';
import type {AuthenticationActions} from 'src/action-creators/referrer-portal/authentication';
import {
  USER_AUTH,
  USER_SIGNIN,
  USER_SIGNIN_ERROR,
  USER_SIGNUP,
  USER_SIGNUP_ERROR,
  USER_RESET_PASSWORD_ERROR,
  RECEIVE_AGENCY,
  RECEIVE_AGENCY_ERROR,
  RECEIVE_USER_DETAILS,
  RECEIVE_USER_DETAILS_ERROR,
  RECEIVE_NOTIFICATION,
  RECEIVE_NOTIFICATION_ERROR,
} from 'src/action-creators/referrer-portal/authentication';
import {
  RECEIVE_ALL_REFERRALS,
  RECEIVE_ALL_REFERRALS_ERROR,
  RECEIVE_ALL_REFERRAL_PROGRAMS,
  RECEIVE_ALL_REFERRAL_PROGRAMS_ERROR,
} from 'src/action-creators/referrer-portal/tracking';
import {
  RECEIVE_JOB_DESCRIPTION,
  RECEIVE_JOB_DESCRIPTION_ERROR,
  RECEIVE_PROGRAM_QUESTIONS,
  RECEIVE_PROGRAM_QUESTIONS_ERROR,
  RECEIVE_REFERRAL_PROGRAMS,
  RECEIVE_REFERRAL_PROGRAMS_ERROR,
  RECEIVE_JOBS,
  RECEIVE_JOBS_ERROR,
  RECEIVE_JOB_LOCATIONS,
  RECEIVE_JOB_LOCATIONS_ERROR,
} from 'src/action-creators/referrer-portal/user-form';
import {
  RECEIVE_EARNINGS,
  RECEIVE_EARNINGS_ERROR,
  RECEIVE_PAYMENTS_DETAILS,
  RECEIVE_PAYMENTS_DETAILS_ERROR,
  RECEIVE_EARNINGS_GRAPH_DATA,
  RECEIVE_EARNINGS_GRAPH_DATA_ERROR,
} from 'src/action-creators/referrer-portal/earnings';

import {
  REFERRAL_STATS,
  REFERRAL_STATS_ERROR,
  REFERRAL_LEADERBOARD,
  REFERRAL_LEADERBOARD_ERROR,
  REFERRAL_SOCIAL_LINKS,
  REFERRAL_SOCIAL_LINKS_ERROR,
  REFERRAL_ACTIVITIES,
  REFERRAL_ACTIVITIES_ERROR,
} from 'src/action-creators/referrer-portal/dashboard';


export type ReferrerPortalState = {
  current_state: string | null | undefined;
  isAuthed: boolean | null | undefined;
  signin_error: string | null | undefined;
  signup_error: string | null | undefined;
  reset_password_error: string | null | undefined;
  agency_list: AgencyList[] | null | undefined;
  agency_list_error: GenericError | null | undefined;
  selected_agency: number | null | undefined;
  selected_agency_error: GenericError | null | undefined;
  account: UserDetails | null | undefined;
  account_error: GenericError | null | undefined;
  referral_tracking_details: GetReferralTrackingApiResponse | null | undefined;
  referral_tracking_details_error: GenericError | null | undefined;
  notification:
    | {
        [key: string]: boolean;
      }
    | null
    | undefined;
  notification_error: GenericError | null | undefined;
  agency_programs: ReferralPrograms[] | null | undefined;
  agency_programs_error: GenericError | null | undefined;
  referral_programs: ReferralPrograms[] | null | undefined;
  referral_programs_error: GenericError | null | undefined;
  jobs: JobsData[] | null | undefined;
  jobs_error: GenericError | null | undefined;
  job_locations: string[] | null | undefined;
  job_locations_error: GenericError | null | undefined;
  job_description: JobDescription | null | undefined;
  job_description_error: GenericError | null | undefined;
  earnings: EarningsData | null | undefined;
  earnings_error: GenericError | null | undefined;
  payment_details: PaymentDetails[] | null | undefined;
  payment_details_error: GenericError | null | undefined;
  earnings_graph_data: EarningsGraphData | null | undefined;
  earnings_graph_data_error: GenericError | null | undefined;
  referral_stats: ReferralStats | null | undefined;
  referral_stats_error: GenericError | null | undefined;
  referral_leaderboard: ReferralLeaderBoard[] | null | undefined;
  referral_leaderboard_error: GenericError | null | undefined;
  referral_social_links: ReferralLinks | null | undefined;
  referral_social_links_error: GenericError | null | undefined;
  referral_activities: RecentActivity[] | null | undefined;
  referral_activities_error: GenericError | null | undefined;
  program_questions: ReferralSelectedQuestionnaire[] | null | undefined;
  program_questions_error: GenericError | null | undefined;
};

const referrerPortalInitialState = {
  current_state: '',
  isAuthed: false,
  signin_error: null,
  signup_error: null,
  reset_password_error: null,
  agency_list: [],
  agency_list_error: null,
  selected_agency: null,
  selected_agency_error: null,
  account: null,
  account_error: null,
  referral_tracking_details: null,
  referral_tracking_details_error: null,
  notification: null,
  agency_programs: null,
  agency_programs_error: null,
  referral_programs: null,
  referral_programs_error: null,
  notification_error: null,
  jobs: null,
  jobs_error: null,
  job_locations: null,
  job_locations_error: null,
  job_description: null,
  job_description_error: null,
  earnings: null,
  payment_details: null,
  payment_details_error: null,
  earnings_error: null,
  earnings_graph_data: null,
  earnings_graph_data_error: null,
  referral_stats: null,
  referral_stats_error: null,
  referral_leaderboard: null,
  referral_leaderboard_error: null,
  referral_social_links: null,
  referral_social_links_error: null,
  referral_activities: null,
  referral_activities_error: null,
  program_questions: null,
  program_questions_error: null,
} as const;

// authentication
export default (
  // @ts-expect-error - TS2322 - Type '{ readonly current_state: ""; readonly isAuthed: false; readonly signin_error: null; readonly signup_error: null; readonly reset_password_error: null; readonly agency_list: readonly []; readonly agency_list_error: null; ... 33 more ...; readonly program_questions_error: null; }' is not assignable to type 'ReferrerPortalState'.
  state: ReferrerPortalState | null | undefined = referrerPortalInitialState,
  action: AuthenticationActions,
): ReferrerPortalState => {
  switch (action.type) {
    case USER_AUTH: {
      // @ts-expect-error - TS2322 - Type '{ isAuthed: boolean; current_state?: string | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; reset_password_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        isAuthed: action.payload,
      };
    }
    case USER_SIGNIN: {
      // @ts-expect-error - TS2322 - Type '{ signin_error: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signup_error?: string | null | undefined; reset_password_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        signin_error: null,
      };
    }
    case USER_SIGNIN_ERROR:
      // @ts-expect-error - TS2322 - Type '{ signin_error: string | null | undefined; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signup_error?: string | null | undefined; reset_password_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        signin_error: action.payload,
      };
    case USER_SIGNUP: {
      return {
        ...state,
        signup_error: null,
        // @ts-expect-error - TS2740 - Type '{ id: number; }' is missing the following properties from type 'UserDetails': name, email, profile_picture_url, phone_number, and 4 more.
        account: {id: action.payload.user_id},
      };
    }
    case USER_SIGNUP_ERROR:
      // @ts-expect-error - TS2322 - Type '{ signup_error: string | null | undefined; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; reset_password_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        signup_error: action.payload,
      };
    case USER_RESET_PASSWORD_ERROR:
      // @ts-expect-error - TS2322 - Type '{ reset_password_error: string | null | undefined; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 36 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        reset_password_error: action.payload,
      };
    case RECEIVE_AGENCY: {
      // @ts-expect-error - TS2322 - Type '{ agency_list: AgencyList[]; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        agency_list: action.payload,
      };
    }
    case RECEIVE_AGENCY_ERROR:
      // @ts-expect-error - TS2322 - Type '{ agency_list_error: GenericError | null | undefined; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 36 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        agency_list_error: action.payload,
      };
    case RECEIVE_USER_DETAILS: {
      // @ts-expect-error - TS2322 - Type '{ account: UserDetails; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        account: action.payload,
      };
    }
    case RECEIVE_USER_DETAILS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ account_error: GenericError | null | undefined; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 36 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        account_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"receive/all-referrals"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_ALL_REFERRALS: {
      // @ts-expect-error - TS2322 - Type '{ referral_tracking_details: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_tracking_details: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/all-referrals-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_ALL_REFERRALS_ERROR: {
      // @ts-expect-error - TS2322 - Type '{ referral_tracking_details_error: any; referral_tracking_details: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_tracking_details_error: action.payload,
        referral_tracking_details: null,
      };
    }
    case RECEIVE_NOTIFICATION: {
      // @ts-expect-error - TS2322 - Type '{ notification: { [key: string]: boolean; }; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        notification: action.payload,
      };
    }
    case RECEIVE_NOTIFICATION_ERROR:
      // @ts-expect-error - TS2322 - Type '{ notification_error: GenericError | null | undefined; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 36 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        notification_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"receive/referral-programs"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_REFERRAL_PROGRAMS: {
      // @ts-expect-error - TS2322 - Type '{ agency_programs: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        agency_programs: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/referral-programs-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_REFERRAL_PROGRAMS_ERROR: {
      // @ts-expect-error - TS2322 - Type '{ agency_programs_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        agency_programs_error: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/jobs"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_JOBS: {
      // @ts-expect-error - TS2322 - Type '{ jobs: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        jobs: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/jobs-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_JOBS_ERROR: {
      // @ts-expect-error - TS2322 - Type '{ jobs_error: any; jobs: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 34 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        jobs_error: action.payload,
        jobs: null,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/job-locations"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_JOB_LOCATIONS: {
      // @ts-expect-error - TS2322 - Type '{ job_locations: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        job_locations: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/job-locations-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_JOB_LOCATIONS_ERROR: {
      // @ts-expect-error - TS2322 - Type '{ job_locations_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        job_locations_error: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/job-description"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_JOB_DESCRIPTION: {
      // @ts-expect-error - TS2322 - Type '{ job_description: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        job_description: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/job-description-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_JOB_DESCRIPTION_ERROR: {
      // @ts-expect-error - TS2322 - Type '{ job_description_error: any; job_description: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 34 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        job_description_error: action.payload,
        job_description: null,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/earnings"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_EARNINGS: {
      // @ts-expect-error - TS2322 - Type '{ earnings: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        earnings: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/earnings-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_EARNINGS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ earnings_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        earnings_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"receive/payment-details"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_PAYMENTS_DETAILS: {
      // @ts-expect-error - TS2322 - Type '{ payment_details: any; payment_details_error: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 34 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        payment_details: action.payload,
        payment_details_error: null,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/payment-details-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_PAYMENTS_DETAILS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ payment_details_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        payment_details_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"receive/earnings-graph-data"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_EARNINGS_GRAPH_DATA: {
      // @ts-expect-error - TS2322 - Type '{ earnings_graph_data: any; earnings_graph_data_error: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        earnings_graph_data: action.payload,
        earnings_graph_data_error: null,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/earnings-graph-data-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_EARNINGS_GRAPH_DATA_ERROR:
      // @ts-expect-error - TS2322 - Type '{ earnings_graph_data_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        earnings_graph_data_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"referral-stats"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_STATS: {
      // @ts-expect-error - TS2322 - Type '{ referral_stats: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_stats: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"referral-stats-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_STATS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ referral_stats_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_stats_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"referrals-leaderboard"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_LEADERBOARD: {
      // @ts-expect-error - TS2322 - Type '{ referral_leaderboard: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_leaderboard: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"referrals-leaderboard-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_LEADERBOARD_ERROR:
      // @ts-expect-error - TS2322 - Type '{ referral_leaderboard_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_leaderboard_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"referrals-social-links"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_SOCIAL_LINKS: {
      // @ts-expect-error - TS2322 - Type '{ referral_social_links: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_social_links: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"referrals-social-links-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_SOCIAL_LINKS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ referral_social_links_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_social_links_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"referrals-activities"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_ACTIVITIES: {
      // @ts-expect-error - TS2322 - Type '{ referral_activities: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_activities: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"referrals-activities-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case REFERRAL_ACTIVITIES_ERROR:
      // @ts-expect-error - TS2322 - Type '{ referral_activities_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_activities_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"receive/program-questions"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_PROGRAM_QUESTIONS: {
      // @ts-expect-error - TS2322 - Type '{ program_questions: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        program_questions: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/program-questions-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_PROGRAM_QUESTIONS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ program_questions_error: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions?: ReferralSelectedQuestionnaire[] | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        program_questions_error: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"receive/all-referral-programs"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_ALL_REFERRAL_PROGRAMS: {
      // @ts-expect-error - TS2322 - Type '{ referral_programs: any; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; signup_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_programs: action.payload,
      };
    }
    // @ts-expect-error - TS2678 - Type '"receive/all-referral-programs-error"' is not comparable to type '"user/signin" | "user/signin-error" | "user/signup" | "user/signup-error" | "user/reset-password" | "user/reset-password-error" | "user/auth" | "receive/agency" | "receive/agency-error" | "receive/user-details" | "receive/user-details-error" | "receive/notification" | "receive/notification-error"'.
    case RECEIVE_ALL_REFERRAL_PROGRAMS_ERROR:
      // @ts-expect-error - TS2322 - Type '{ referral_programs_error: any; referral_programs: null; current_state?: string | null | undefined; isAuthed?: boolean | null | undefined; signin_error?: string | null | undefined; ... 35 more ...; program_questions_error?: GenericError | null | undefined; }' is not assignable to type 'ReferrerPortalState'.
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        referral_programs_error: action.payload,
        referral_programs: null,
      };
    default:
      // @ts-expect-error - TS2322 - Type 'ReferrerPortalState | null' is not assignable to type 'ReferrerPortalState'.
      return state;
  }
};
